import { Container, Graphics, Rectangle } from 'pixi.js';
import { COLORS, DESIGN_W, SCRIM } from '../app/theme';
import { viewport } from '../app/viewport';
import { Panel, makeText } from './widgets';

export interface ModalCardOpts {
  width?: number;
  height?: number;
  title?: string;
  /** When provided, tapping the dimmed scrim outside the card fires this (tap-outside-to-dismiss). */
  onDismiss?: () => void;
}

/**
 * A reusable modal: a full-design-space scrim that swallows taps + a centered NEAR-OPAQUE Panel card
 * with an optional gold title and a `content` container for callers to lay their widgets into. The
 * card is forced to alpha ~0.98 (Panel defaults to 0.85, which is why the old avatar/password overlays
 * looked see-through). Mirrors Dialog.ts but exposes the content area for custom layouts. Toggle with
 * `.visible` or add/remove from a parent; on scene teardown SceneManager destroys it with the scene.
 */
export class ModalCard extends Container {
  /** Lay your widgets here; (0,0) is the card's top-left corner. */
  readonly content = new Container();
  readonly cardWidth: number;
  readonly cardHeight: number;
  readonly cardX: number;
  readonly cardY: number;
  /** Y (relative to the card top-left) just below the title — where caller content should begin. */
  readonly contentTop: number;

  constructor(opts: ModalCardOpts = {}) {
    super();
    const w = opts.width ?? DESIGN_W - 80;
    const h = opts.height ?? 760;
    this.cardWidth = w;
    this.cardHeight = h;
    this.cardX = (DESIGN_W - w) / 2;
    this.cardY = Math.max(0, (viewport.h - h) / 2);

    const scrim = new Graphics().rect(0, 0, DESIGN_W, viewport.h).fill({ color: COLORS.scrim, alpha: SCRIM.modal });
    scrim.eventMode = 'static'; // swallow taps to the page behind
    if (opts.onDismiss) {
      scrim.cursor = 'pointer';
      scrim.on('pointertap', opts.onDismiss);
    }
    this.addChild(scrim);

    const panel = new Panel(w, h, { fill: COLORS.panel, alpha: 0.98 });
    panel.position.set(this.cardX, this.cardY);
    // Capture taps over the card so they don't fall through to the dismiss scrim below.
    panel.eventMode = 'static';
    panel.hitArea = new Rectangle(0, 0, w, h);
    this.addChild(panel);

    this.content.position.set(this.cardX, this.cardY);
    this.addChild(this.content);

    let top = 28;
    if (opts.title) {
      const t = makeText(opts.title, { size: 28, weight: '700', align: 'center', fill: COLORS.gold });
      t.anchor.set(0.5, 0);
      t.position.set(w / 2, top);
      this.content.addChild(t);
      top += 56;
    }
    this.contentTop = top;
  }
}
